"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/PlaidImportModal/PortfolioConnectionCard";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React from "react";
import { cn } from "utils";

import {
  getConnectionDisplayInfo,
  type AccountDisplayInfo,
} from "./getConnectionAccountMasks";
import { PlaidInstitutionLogo } from "./PlaidInstitutionLogo";
import type { FinancialConnection } from "@features/api/memberApi";

const I18n = I18nUtil.createScoped(Translations);

export interface PortfolioConnectionCardProps {
  connection: FinancialConnection;
  /** "invest" → INVESTMENT accounts only; "non_invest" → non-INVESTMENT accounts only. */
  section: "invest" | "non_invest";
  disabled?: boolean;
  onManage: () => void;
}

/** Check if a connection is a manual upload (platform === "MANUAL"). */
const isManualConnection = (connection: FinancialConnection): boolean =>
  connection.platform === "MANUAL";

/** Chunk account display info into rows of 2. */
const chunkDisplayInfoRows = (
  accounts: AccountDisplayInfo[],
  rowSize = 2,
): AccountDisplayInfo[][] => {
  if (accounts.length === 0) {
    return [];
  }
  const rows: AccountDisplayInfo[][] = [];
  for (let i = 0; i < accounts.length; i += rowSize) {
    rows.push(accounts.slice(i, i + rowSize));
  }
  return rows;
};

export const PortfolioConnectionCard: React.FC<
  PortfolioConnectionCardProps
> = ({ connection, section, disabled = false, onManage }) => {
  const isManual = isManualConnection(connection);
  const displayInfo = getConnectionDisplayInfo(connection, { section });
  const accountRows = chunkDisplayInfoRows(displayInfo);
  const connectionId = connection.connection_id;

  return (
    <div
      className="flex flex-col rounded-3 bg-background_solid_neutral_primary px-4 py-3"
      data-testid="portfolio-connection-card"
    >
      {/* Institution header */}
      <div className="flex flex-wrap items-center justify-between gap-y-3 border-b border-border_inverse_transparent10 pb-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {isManual ? (
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-background_white_overlay5 p-2.5">
              <Icon
                name="bank"
                size={20}
                color={ospThemes.colors.content_primary}
              />
            </div>
          ) : (
            <PlaidInstitutionLogo
              institutionName={connection.institution_name}
              logo={connection.institution_logo}
            />
          )}
          <p className="body_bold_m truncate text-content_primary">
            {isManual
              ? I18n.t("Manual upload")
              : (connection.institution_name ??
                I18n.t("~Unknown institution~"))}
          </p>
        </div>
        {/* AC-2.5: Manual connections do not show Manage button */}
        {!isManual ? (
          <button
            type="button"
            disabled={disabled || !connectionId}
            aria-label={`Manage ${connection.institution_name ?? "connection"}`}
            className={cn(
              "inline-flex shrink-0 items-center gap-0.5 button_s text-content_brand_primary",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            onClick={onManage}
            data-testid="portfolio-connection-manage"
          >
            {I18n.t("Manage")}
            <Icon name="expand-alt" size={16} className="shrink-0" />
          </button>
        ) : null}
      </div>
      {/* Sub-account rows */}
      {displayInfo.length > 0 ? (
        <div className="flex flex-col pl-11 pt-1">
          {accountRows.map((row, rowIndex) => (
            <div
              key={`${connectionId ?? connection.institution_id}-row-${rowIndex}`}
              className="grid grid-cols-2 py-2"
            >
              {row.map((account, index) => (
                <div
                  className="inline-flex items-center gap-1 py-0.5 body_medium_s text-content_secondary"
                  key={`${account.name}-${account.mask}-${index}`}
                >
                  <Icon name="wallet" size={12} className="shrink-0" />
                  {/* Audit #7 fix: text-[#01262b] → text-content_primary (name) */}
                  <span className="body_medium_s text-content_primary truncate">
                    {account.name}
                  </span>
                  {/* Audit #8 fix: removed text-[0px] hack — mask inherits body_s */}
                  <span className="body_s text-content_secondary">
                    {account.mask}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
